/**
 * Header Slice
 * 
 * Manages global header state (replaces HeaderContext).
 * 
 * Key improvements over HeaderContext:
 * - Stores configuration, not ReactNode (serializable, stable references)
 * - Owner-based cleanup prevents race conditions
 * - Selective subscriptions via Zustand
 * - No provider needed
 */

import type { StateCreator } from 'zustand';
import type { BoundStoreState, HeaderSlice, HeaderActionButton } from '../types';

// ============================================================================
// Initial State
// ============================================================================

export const headerSliceInitialState = {
  headerOwnerId: null as string | null,
  showBackButton: true,
  backButtonPath: null as string | null,
  actionButtons: [] as HeaderActionButton[],
};

// ============================================================================
// Slice Creator
// ============================================================================

/**
 * Creates the header slice.
 */
export const createHeaderSlice: StateCreator<
  BoundStoreState,
  [['zustand/devtools', never]],
  [],
  HeaderSlice
> = (set, get) => ({
  // State
  ...headerSliceInitialState,

  // Actions
  setHeaderConfig: (ownerId, config) =>
    set(
      {
        headerOwnerId: ownerId,
        showBackButton: config.showBackButton ?? get().showBackButton,
        backButtonPath: config.backButtonPath ?? get().backButtonPath,
        actionButtons: config.actionButtons ?? get().actionButtons,
      },
      undefined,
      'header/setConfig'
    ),

  clearHeaderConfig: (ownerId) => {
    // Only clear if THIS component owns the current header state
    // Prevents race conditions when navigating between pages
    if (get().headerOwnerId === ownerId) {
      set(
        headerSliceInitialState,
        undefined,
        'header/clearConfig'
      );
    }
  },

  resetHeader: () =>
    set(
      headerSliceInitialState,
      undefined,
      'header/reset'
    ),
});
